import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Isolates one draft question's render.
 *
 * Without this a single malformed question takes the whole review dialog down
 * with it — React unmounts the subtree and the author sees a blank panel with
 * no indication of which question failed or why. One bad question should cost
 * one row, and it should say what went wrong.
 */
interface Props {
  /** 1-based position, shown so the failing question can be identified. */
  index: number;
  type: string;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class QuestionCardBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the full trace in the console — the visible row is deliberately terse.
    // eslint-disable-next-line no-console
    console.error(
      `[GeneratedQuestionsDialog] question #${this.props.index} (${this.props.type}) failed to render`,
      error,
      info.componentStack,
    );
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex items-start gap-2 border-b border-border py-3 text-[13px] text-error-700 dark:text-error-300">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium">
            Question {this.props.index} ({this.props.type}) could not be displayed
          </p>
          <p className="break-words text-xs text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }
}

export default QuestionCardBoundary;
